import { HubConnectionBuilder, LogLevel } from "@microsoft/signalr";

/* ------------------------------------------------------------------ *
 *  Task Manager API client. Every screen talks to the gateway through
 *  this one file: sign-in token, errors, camelCase answers, and the
 *  calls the Task Manager and its pages share.
 * ------------------------------------------------------------------ */

export const TASKS_API = "https://tasks.cilpron.com";

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

export const readToken = (): string | null => {
  try {
    return localStorage.getItem("authToken");
  } catch {
    return null;
  }
};

/** Some answers come straight from SQL (PascalCase), others from the CRUD controllers (camelCase). Everything is turned into camelCase here. */
const camelKey = (k: string) => (k.length > 1 && k === k.toUpperCase() ? k.toLowerCase() : k.charAt(0).toLowerCase() + k.slice(1));
export const camelize = (v: unknown): unknown => {
  if (Array.isArray(v)) return v.map(camelize);
  if (v && typeof v === "object") {
    const o: Record<string, unknown> = {};
    for (const [k, x] of Object.entries(v as Record<string, unknown>)) o[camelKey(k)] = camelize(x);
    return o;
  }
  return v;
};

type Query = Record<string, string | number | boolean | null | undefined>;
const qs = (q?: Query) => {
  if (!q) return "";
  const p = Object.entries(q)
    .filter(([, v]) => v !== undefined && v !== null && v !== "")
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  return p.length ? `?${p.join("&")}` : "";
};

export async function api<T = unknown>(method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE", path: string, body?: unknown, query?: Query): Promise<T> {
  const token = readToken();
  const headers: Record<string, string> = { Accept: "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;
  if (body !== undefined) headers["Content-Type"] = "application/json";
  let res: Response;
  try {
    res = await fetch(`${TASKS_API}${path}${qs(query)}`, { method, headers, ...(body === undefined ? {} : { body: JSON.stringify(body) }) });
  } catch {
    throw new ApiError(0, "Couldn’t reach the Task Manager. Check your connection.");
  }
  if (res.status === 401) throw new ApiError(401, "Your session ended. Sign in again.");
  if (!res.ok) {
    let msg = "";
    try {
      const j = (await res.json()) as Record<string, unknown>;
      const pick = j.title ?? j.detail ?? j.message ?? j.error;
      if (typeof pick === "string") msg = pick;
    } catch {
      /* no readable body */
    }
    throw new ApiError(res.status, msg || (res.status === 403 ? "You don’t have permission to do that." : `The Task Manager answered with an error (${res.status}).`));
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? camelize(JSON.parse(text)) : undefined) as T;
}

export const msgOf = (e: unknown): string => (e instanceof ApiError ? e.message : "Something went wrong. Try again.");

/* ---------- CRUD controllers: api/{service}/{schema}/{table} ---------- */
export interface Paged<T> {
  items: T[];
  page: number;
  pageSize: number;
  total: number | null;
}
export const crud = {
  list: <T,>(route: string, query?: Query) => api<Paged<T>>("GET", `/api/${route}`, undefined, { pageSize: 200, ...query }),
  get: <T,>(route: string, id: number) => api<T>("GET", `/api/${route}/${id}`),
  create: <T,>(route: string, body: Record<string, unknown>) => api<T>("POST", `/api/${route}`, body),
  update: <T,>(route: string, id: number, body: Record<string, unknown>) => api<T>("PATCH", `/api/${route}/${id}`, body),
  remove: (route: string, id: number) => api<void>("DELETE", `/api/${route}/${id}`),
};

/* ---------- routes ---------- */
export const R = {
  workspace: "workspaces/ws/workspace",
  member: "workspaces/ws/workspacemember",
  project: "workspaces/prj/project",
  status: "workspaces/prj/status",
  user: "identity/sec/appuser",
  task: "tasks/tsk/task",
  assignee: "tasks/tsk/taskassignee",
  comment: "tasks/tsk/comment",
  checklist: "tasks/tsk/checklist",
  checkitem: "tasks/tsk/checklistitem",
  rule: "automation/auto/rule",
  goal: "goals/gl/goal",
  keyResult: "goals/gl/keyresult",
  sprint: "agile/agl/sprint",
  document: "knowledge/kb/document",
  ticket: "intake/svc/ticket",
  webhook: "integration/int/webhook",
  timeEntry: "time/tm/timeentry",
};

/* ---------- shapes ---------- */
export type Priority = "Low" | "Medium" | "High";
export const PRIORITIES: Priority[] = ["Low", "Medium", "High"];
/** The API knows NONE, LOW, MEDIUM, HIGH and URGENT; the screens show three. */
export const prioFromApi = (p?: string | null): Priority => {
  const v = (p ?? "").toUpperCase();
  return v === "HIGH" || v === "URGENT" ? "High" : v === "MEDIUM" ? "Medium" : "Low";
};
export const prioToApi = (p: Priority) => p.toUpperCase();

export interface WorkspaceRef {
  id: number;
  name: string;
  kind: string;
  role: string;
}
export interface Me {
  id: number;
  subject: string;
  email?: string;
  displayName?: string;
  locale: string;
  timeZone: string;
  currencyCode?: string;
  defaultWorkspaceId?: number | null;
  workspaces: WorkspaceRef[];
  permissions: string[];
}
export interface Project {
  id: number;
  workspaceId: number;
  name: string;
  projectKey?: string | null;
  description?: string | null;
  color?: string | null;
  status: string;
  projectType: string;
  defaultView: string;
  isArchived: boolean;
  health?: string | null;
}
export interface Person {
  id: number;
  name: string;
  email: string;
  role: string;
}
export interface BoardAssignee {
  userId: number;
  displayName: string;
}
export interface BoardCard {
  id: number;
  title: string;
  description?: string | null;
  priority: string;
  startDate?: string | null;
  dueDate?: string | null;
  dueAt?: string | null;
  isCompleted: boolean;
  storyPoints?: number | null;
  estimateMinutes?: number | null;
  sortOrder: number;
  parentTaskId?: number | null;
  attachmentCount: number;
  commentCount: number;
  milestonesDone: number;
  milestonesTotal: number;
  subtaskCount: number;
  assignees: BoardAssignee[] | null;
}
export interface BoardColumn {
  id: number;
  name: string;
  category: string;
  color?: string | null;
  wipLimit?: number | null;
  sortOrder: number;
  taskCount: number;
  tasks: BoardCard[] | null;
}

/* ---------- dates ---------- */
const pad = (n: number) => String(n).padStart(2, "0");
export const iso = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
export const parseISO = (s: string) => {
  const [y, m, d] = s.slice(0, 10).split("-").map(Number);
  return new Date(y || 1970, (m || 1) - 1, d || 1);
};
/** SQL date-times arrive without a zone; they are UTC. */
export const utc = (s: string) => new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(s) ? s : `${s}Z`);
export const hhmmOf = (s?: string | null) => {
  if (!s) return "";
  const d = utc(s);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
/** a local date + HH:mm as a UTC instant for the API */
export const toUtcIso = (date: string, time: string) => {
  const [y, m, d] = date.split("-").map(Number);
  const [h, mi] = time.split(":").map(Number);
  return new Date(y || 1970, (m || 1) - 1, d || 1, h || 0, mi || 0).toISOString();
};

/* ---------- calls the Task Manager shell uses ---------- */
export const getMe = () => api<Me>("GET", "/api/identity/me");

export const getProjects = async (workspaceId: number) =>
  (await crud.list<Project>(R.project, { workspaceId, isArchived: false, sort: "name" })).items;

export const getBoard = async (projectId: number) => (await api<BoardColumn[]>("GET", "/api/views/board", undefined, { projectId, includeDone: true })) ?? [];

/** People of a workspace with their names (member list joined with the user directory). */
export const getPeople = async (workspaceId: number): Promise<Person[]> => {
  const members = (await crud.list<{ userId: number; role: string; isActive: boolean }>(R.member, { workspaceId, isActive: true })).items;
  if (members.length === 0) return [];
  const users = (await crud.list<{ id: number; displayName?: string | null; email?: string | null }>(R.user, { "id.in": members.map((m) => m.userId).join(","), pageSize: 500 })).items;
  const by = new Map(users.map((u) => [u.id, u]));
  return members.map((m) => {
    const u = by.get(m.userId);
    return { id: m.userId, name: u?.displayName || u?.email || `User ${m.userId}`, email: u?.email ?? "", role: m.role };
  });
};

export const moveTask = (taskId: number, statusId: number, beforeTaskId: number | null) =>
  api("POST", `/api/tasks/${taskId}/move`, { statusId, beforeTaskId });

export interface NewTask {
  workspaceId: number;
  projectId: number;
  statusId: number;
  title: string;
  description: string;
  priority: Priority;
  dueDate: string;
  time: string;
  assigneeIds: number[];
}
export const createTask = async (v: NewTask) => {
  const t = await crud.create<{ id: number }>(R.task, {
    workspaceId: v.workspaceId,
    projectId: v.projectId,
    statusId: v.statusId,
    title: v.title,
    description: v.description || null,
    priority: prioToApi(v.priority),
    startDate: iso(new Date()),
    dueDate: v.dueDate,
    dueAt: toUtcIso(v.dueDate, v.time),
  });
  for (const userId of v.assigneeIds) await crud.create(R.assignee, { workspaceId: v.workspaceId, taskId: t.id, userId });
  return t;
};

export const deleteTask = (id: number) => crud.remove(R.task, id);

export interface TaskDetail {
  id: number;
  workspaceId: number;
  projectId?: number | null;
  title: string;
  description?: string | null;
  statusId?: number | null;
  priority: string;
  startDate?: string | null;
  dueDate?: string | null;
  dueAt?: string | null;
  isCompleted: boolean;
  estimateMinutes?: number | null;
  assignees: { userId: number; isPrimary: boolean; displayName: string }[] | null;
  subtasks: { id: number; title: string; isCompleted: boolean; dueDate?: string | null }[] | null;
  checklists: { id: number; title: string; items: { id: number; text: string; isDone: boolean }[] | null }[] | null;
  tags: { id: number; name: string; color?: string | null }[] | null;
}
export const getTaskDetail = (id: number) => api<TaskDetail>("GET", `/api/tasks/${id}/detail`);

export interface CommentRow {
  id: number;
  taskId: number;
  body: string;
  createdAt: string;
  createdBy?: number | null;
  isEdited: boolean;
}
export const getComments = async (taskId: number) => (await crud.list<CommentRow>(R.comment, { taskId, sort: "createdAt" })).items;
export const addComment = (workspaceId: number, taskId: number, body: string) => crud.create<CommentRow>(R.comment, { workspaceId, taskId, body });

export const setItemDone = (id: number, isDone: boolean) => crud.update(R.checkitem, id, { isDone });

/* ---------- live updates ---------- */
export interface LiveHandlers {
  onProject?: () => void;
  onNotification?: () => void;
}
/** Connects to the live feed of one project. Returns a function that disconnects. */
export function connectLive(projectId: number | null, h: LiveHandlers): () => void {
  if (!readToken()) return () => undefined;
  let dead = false;
  let timer = 0;
  const later = (fn?: () => void) => {
    window.clearTimeout(timer);
    timer = window.setTimeout(() => fn?.(), 400);
  };
  const conn = new HubConnectionBuilder()
    .withUrl(`${TASKS_API}/hubs/tasks`, { accessTokenFactory: () => readToken() ?? "" })
    .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
    .configureLogging(LogLevel.None)
    .build();
  conn.on("ProjectEvent", () => later(h.onProject));
  conn.on("Notification", () => later(h.onNotification));
  const join = () => {
    if (projectId !== null) void conn.invoke("JoinProject", projectId).catch(() => undefined);
  };
  conn.onreconnected(join);
  conn.start().then(() => {
    if (!dead) join();
  }).catch(() => undefined);
  return () => {
    dead = true;
    window.clearTimeout(timer);
    void conn.stop();
  };
}